import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Wallet, Trash } from "lucide-react";
import { api, fmtINR, fmtDate, apiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";

const MODES = ["Cash", "Bank", "UPI", "Cheque", "NEFT"];
const tone = (s) => (s === "Recovered" ? "bg-emerald-50 text-emerald-700" : s === "Partially Recovered" ? "bg-amber-50 text-amber-700" : "bg-slate-100 text-slate-700");

const KINDS = {
  "daily-workers": { deleteBase: "worker-advances", note: "Money given in advance leaves cash/bank today and is automatically deducted from this worker's next wage statement." },
  employees: { deleteBase: "employee-advances", note: "The salary advance leaves cash/bank today and is automatically deducted from this employee's next salary slip." },
};

export const WorkerAdvanceDialog = ({ worker, basePath = "daily-workers", onClose, canWrite }) => {
  const kind = KINDS[basePath] || KINDS["daily-workers"];
  const [d, setD] = useState(null);
  const [form, setForm] = useState({ amount: "", advance_date: new Date().toISOString().slice(0, 10), payment_mode: "Cash", reference_number: "", notes: "" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try { const { data } = await api.get(`/${basePath}/${worker.id}/advances`); setD(data); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [worker?.id]);

  const save = async () => {
    if (!(Number(form.amount) > 0)) return toast.error("Enter the advance amount");
    setSaving(true);
    try {
      await api.post(`/${basePath}/${worker.id}/advances`, { ...form, amount: Number(form.amount) });
      toast.success(`Advance recorded — it will be deducted from the next ${basePath === "employees" ? "salary slip" : "wages"}`);
      setForm({ ...form, amount: "", reference_number: "", notes: "" });
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setSaving(false);
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this advance entry?")) return;
    try { await api.delete(`/${kind.deleteBase}/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <Dialog open={!!worker} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="worker-advance-dialog">
        <DialogHeader>
          <DialogTitle className="font-head flex items-center gap-2"><Wallet className="w-5 h-5" /> Advances · {worker?.name}</DialogTitle>
          <DialogDescription>{kind.note}</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-3 gap-3">
          {[["Given", d?.total_given, "text-slate-900"], ["Recovered", d?.total_recovered, "text-emerald-700"], ["Outstanding", d?.outstanding, "text-red-600"]].map(([l, v, c]) => (
            <div key={l} className="bg-white border border-slate-200 rounded-sm p-3">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{l}</div>
              <div className={`text-base font-mono font-semibold mt-1 ${c}`} data-testid={`advance-${l.toLowerCase()}`}>{fmtINR(v)}</div>
            </div>))}
        </div>

        {canWrite && (
          <div className="border border-slate-200 rounded-sm p-3">
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Record new advance</div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              <Input data-testid="advance-amount" type="number" min="0" placeholder="Amount ₹" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="h-9" />
              <Input data-testid="advance-date" type="date" value={form.advance_date} onChange={(e) => setForm({ ...form, advance_date: e.target.value })} className="h-9" />
              <Select value={form.payment_mode} onValueChange={(v) => setForm({ ...form, payment_mode: v })}>
                <SelectTrigger data-testid="advance-mode" className="h-9"><SelectValue /></SelectTrigger>
                <SelectContent>{MODES.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
              <Input data-testid="advance-ref" placeholder="Reference / UTR" value={form.reference_number} onChange={(e) => setForm({ ...form, reference_number: e.target.value })} className="h-9" />
              <Input data-testid="advance-notes" placeholder="Reason / notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="h-9 sm:col-span-2" />
            </div>
            <div className="text-right mt-2">
              <Button data-testid="advance-save" disabled={saving} onClick={save} className="bg-[#0B132B] hover:bg-[#1C2541] text-white rounded-sm">{saving ? "Saving..." : "Add Advance"}</Button>
            </div>
          </div>
        )}

        <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="bg-slate-50 border-b border-slate-200 text-left">
              {["Advance No", "Date", "Amount", "Recovered", "Balance", "Mode", "Notes", "Status", ""].map((h, i) => (
                <th key={i} className="px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-slate-500">{h}</th>))}
            </tr></thead>
            <tbody>
              {!d ? <tr><td colSpan={9} className="px-3 py-8 text-center text-slate-400">Loading...</td></tr>
                : !d.rows.length ? <tr><td colSpan={9} className="px-3 py-8 text-center text-slate-400" data-testid="advance-empty">No advance given yet</td></tr>
                  : d.rows.map((a) => (
                    <tr key={a.id} data-testid={`advance-row-${a.id}`} className="border-b border-slate-100">
                      <td className="px-3 py-2 font-medium text-slate-800 whitespace-nowrap">{a.advance_no}</td>
                      <td className="px-3 py-2 whitespace-nowrap text-slate-600">{fmtDate(a.advance_date)}</td>
                      <td className="px-3 py-2 font-mono tabular-nums">{fmtINR(a.amount)}</td>
                      <td className="px-3 py-2 font-mono tabular-nums text-emerald-700">{fmtINR(a.recovered_amount)}</td>
                      <td className="px-3 py-2 font-mono tabular-nums text-red-600 font-semibold">{fmtINR(a.balance)}</td>
                      <td className="px-3 py-2 text-slate-600">{a.payment_mode}</td>
                      <td className="px-3 py-2 text-slate-500 truncate max-w-[160px]">{a.notes || "-"}</td>
                      <td className="px-3 py-2"><Badge variant="outline" className={`rounded-full px-2.5 ${tone(a.status)}`}>{a.status}</Badge></td>
                      <td className="px-3 py-2 text-right">
                        {canWrite && <button type="button" data-testid={`advance-delete-${a.id}`} onClick={() => remove(a.id)} className="text-slate-400 hover:text-red-600"><Trash className="w-4 h-4" /></button>}
                      </td>
                    </tr>))}
            </tbody>
          </table>
        </div>

        <DialogFooter><Button variant="outline" className="rounded-sm" onClick={onClose}>Close</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
